/**
 * Calculates the total number of pages based on total results and page size.
 * @param {number} totalResults - Total number of available results.
 * @param {number} pageSize - Number of results per page.
 * @returns {number} Total number of pages.
 */
export function getTotalPages(totalResults, pageSize) {
  return Math.ceil(totalResults / pageSize);
}

/**
 * Generates a sorted list of page numbers to display in the pagination bar.
 * The list adapts based on the current page and the maximum number of pages to show.
 * @param {number} totalPages - Total number of available pages.
 * @param {number} currentPage - Currently active page.
 * @param {number} [maxPagesToShow=7] - Maximum number of page links to display.
 * @returns {number[]} Array of page numbers to render.
 */
export function getPageNumberList(
  totalPages,
  currentPage,
  maxPagesToShow = 7
) {
  if (totalPages <= maxPagesToShow) {
    return Array.from(
      { length: totalPages },
      (_, i) => i + 1
    );
  }

  const pages = new Set();

  // Always include first and last pages
  pages.add(1);
  pages.add(totalPages);

  // Include current page and its immediate neighbors
  for (let i = currentPage - 1; i <= currentPage + 1; i += 1) {
    if (i > 1 && i < totalPages) {
      pages.add(i);
    }
  }

  // Include early pages when close to the beginning
  if (currentPage <= 4) {
    [2, 3, 4, 5].forEach((p) => pages.add(p));
  }

  // Include ending pages when close to the end
  if (currentPage >= totalPages - 3) {
    [
      totalPages - 1,
      totalPages - 2,
      totalPages - 3,
      totalPages - 4
    ].forEach((p) => pages.add(p));
  }

  return Array.from(pages).sort((a, b) => a - b);
}
